/**
 * RTK Query endpoints — Auth bootstrap (post-Cognito HTTP)
 *
 * Migrated from: src/pages/login/actions.js (HTTP portion only).
 *
 * The Cognito challenge flow (MFA, new password) stays as thunks — those
 * are SDK calls, not HTTP. This file covers the three HTTP calls the
 * login flow + workspace dropdown drive:
 *   GET  /su/auth/me               — current user (incl. permissions)
 *   GET  /su/auth/get-workspaces   — tenants the current user can access
 *   POST /su/auth/change-workspace — switch active tenant
 *
 * `useMeQuery` and `useWorkspacesQuery` mirror back into the legacy
 * `state.user` slice via onQueryStarted so the existing selectors
 * (selectUser, selectWorkspaces, hasPermission) keep working without
 * touching every consumer.
 *
 * `changeWorkspace` dispatches setSession with the new tenant payload
 * and resets the RTK Query cache — the tenant scope just changed, so
 * every cached query is now stale.
 */

import { baseApi } from './baseApi';
import { setSession } from '../../session/actions';

const AUTH_GET_WORKSPACES_SUCCESS = 'AUTH_GET_WORKSPACES_SUCCESS';
const AUTH_LOGIN_SUCCESS = 'AUTH_LOGIN_SUCCESS';

const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    me: builder.query({
      query: () => ({ url: '/su/auth/me', method: 'GET' }),
      providesTags: [{ type: 'AuthMe', id: 'CURRENT' }],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled, getState }) => {
        try {
          const { data } = await queryFulfilled;
          const current = getState().session?.current || {};
          dispatch(setSession({ ...current, user: data }));
        } catch {
          // surfaced via query error state
        }
      },
    }),

    workspaces: builder.query({
      query: () => ({ url: '/su/auth/get-workspaces', method: 'GET' }),
      // Accepts both new `{ workspaces, predefinedTenant }` and the legacy
      // bare-array response.
      transformResponse: (data) => {
        if (Array.isArray(data)) return { workspaces: data };
        return data || { workspaces: [] };
      },
      providesTags: [{ type: 'Workspaces', id: 'LIST' }],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch({
            type: AUTH_GET_WORKSPACES_SUCCESS,
            payload: data?.workspaces ?? [],
          });
        } catch {
          // surfaced via query error state
        }
      },
    }),

    changeWorkspace: builder.mutation({
      query: ({ workspaceId }) => ({
        url: '/su/auth/change-workspace',
        method: 'POST',
        body: { workspaceId },
      }),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(setSession(data));
          dispatch({ type: AUTH_LOGIN_SUCCESS, payload: data });
          // Tenant scope changed — drop every cached query.
          dispatch(baseApi.util.resetApiState());
        } catch {
          // surfaced via mutation error state
        }
      },
    }),

    verify2FA: builder.mutation({
      query: ({ accountId, token }) => ({
        url: '/su/auth/2fa-verify',
        method: 'POST',
        body: { accountId, token },
      }),
      extraOptions: { skipAuth: true, skipTenant: true },
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(setSession(data));
        } catch {
          // surfaced via mutation error state
        }
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useMeQuery,
  useLazyMeQuery,
  useWorkspacesQuery,
  useLazyWorkspacesQuery,
  useChangeWorkspaceMutation,
  useVerify2FAMutation,
} = authApi;

export default authApi;
